import { images } from './images'

export type Service = {
  name: string
  description: string
  image?: string
  imageAlt?: string
  feature?: boolean
}

export type ServiceCategory = {
  id: string
  label: string
  intro: string
  services: Service[]
}

export const serviceCategories: ServiceCategory[] = [
  {
    id: 'general',
    label: 'General & Preventive',
    intro:
      'The foundation of a healthy mouth: regular exams, professional cleanings and early detection so small problems stay small.',
    services: [
      {
        name: 'Dental cleanings',
        description:
          'Professional hygiene that removes plaque and tartar to keep your gums firm and healthy.',
        image: images.services.prevention,
        imageAlt:
          'Dr. Julio Lima talking with a patient seated in the dental chair at Lima Dental Group',
        feature: true,
      },
      {
        name: 'Dental exams',
        description:
          'A complete look at your teeth, gums and bite, with a clear explanation of what we find.',
      },
      {
        name: 'Oral cancer screenings',
        description:
          'A short, painless screening we include in your routine visit to catch early signs.',
      },
      {
        name: 'Fillings',
        description:
          'Tooth-colored restorations that treat decay and protect your natural tooth structure.',
      },
      {
        name: 'Sealants',
        description:
          'A protective layer over the back teeth, especially helpful for children and teens.',
      },
      {
        name: 'Extractions',
        description:
          'When a tooth can no longer be saved, we remove it with proper anesthesia and follow-up.',
      },
      {
        name: 'Night guards',
        description:
          'Custom-made guards for patients who clench or grind their teeth while sleeping.',
      },
    ],
  },
  {
    id: 'restorative',
    label: 'Restorative',
    intro:
      'Restoring function, shape and comfort when a tooth is damaged or missing, with solutions built to last.',
    services: [
      {
        name: 'Dental implants',
        description:
          'The most stable way to replace one or several missing teeth, with a natural look and feel.',
        image: images.services.implant,
        imageAlt: 'Close-up of a titanium dental implant on a matte dark surface',
        feature: true,
      },
      {
        name: 'Crowns',
        description:
          'Cover and protect a weakened or restored tooth, bringing back its strength.',
      },
      {
        name: 'Bridges',
        description:
          'Replace one or more missing teeth by anchoring to the neighboring teeth.',
      },
      {
        name: 'Dentures',
        description:
          'Custom full dentures that restore your ability to eat and speak comfortably.',
      },
      {
        name: 'Partial dentures',
        description:
          'For patients who keep some natural teeth and need to replace the rest.',
      },
      {
        name: 'Root canal therapy',
        description:
          'Treats infection inside the tooth to relieve pain and keep the tooth in place.',
      },
      {
        name: 'Damaged tooth repair',
        description:
          'Care for chipped, cracked or worn teeth, restoring both strength and appearance.',
      },
    ],
  },
  {
    id: 'cosmetic',
    label: 'Cosmetic',
    intro:
      'Aesthetic improvements planned with a natural eye — nothing exaggerated, nothing that does not look like you.',
    services: [
      {
        name: 'Smile design',
        description:
          'We plan shape, proportion and shade together before starting any treatment.',
        image: images.services.cosmetic,
        imageAlt: 'Adult patient smiling naturally and at ease',
        feature: true,
      },
      {
        name: 'Veneers',
        description:
          'Thin porcelain layers that correct color, shape or minor wear.',
      },
      {
        name: 'Teeth whitening',
        description:
          'We brighten your shade in a controlled way while protecting your enamel.',
      },
      {
        name: 'Invisalign',
        description:
          'Clear, removable aligners that straighten your teeth discreetly.',
      },
    ],
  },
  {
    id: 'emergency',
    label: 'Emergency',
    intro:
      'If you have dental pain or an urgent concern, call Lima Dental Group directly to request care.',
    services: [
      {
        name: 'Dental pain',
        description:
          'We identify where the pain is coming from and tell you the next step clearly.',
        image: images.services.emergency,
        imageAlt:
          'Dr. Julio Lima examining a patient with a dental mirror at Lima Dental Group',
        feature: true,
      },
      {
        name: 'Broken tooth',
        description:
          'Evaluation and restoration of teeth fractured or chipped by impact or wear.',
      },
      {
        name: 'Infections',
        description:
          'Care for abscesses and swelling that need treatment without delay.',
      },
      {
        name: 'Urgent extraction',
        description: 'When a tooth cannot be saved and needs to be removed promptly.',
      },
      {
        name: 'Emergency evaluations',
        description: 'A focused exam of your urgent concern, with a clear plan to resolve it.',
      },
    ],
  },
]

/** Appointment form options. */
export const appointmentServices = [
  'Cleaning & dental exam',
  'Dental emergency',
  'Invisalign',
  'Dental implants',
  'Crowns, bridges or dentures',
  'Whitening or veneers',
  'Root canal therapy',
  'General consultation',
]
